import { useState, useEffect } from "react";

const API_BASE = import.meta.env.VITE_AI_GATEWAY_URL || "";
const AUTH_BASE = import.meta.env.VITE_AUTH_URL || "https://auth.mattherbert.ca";

// Types
interface Model {
  id: string;
  model_id: string;
  display_name: string;
  provider: string;
  location: "local" | "edge" | "remote";
  quality_score: number;
  speed_score: number;
  coding_score: number;
  reasoning_score: number;
  privacy_score: number;
  context_window: number;
  input_cost_per_1m_tokens_usd: number;
  output_cost_per_1m_tokens_usd: number;
  enabled: boolean;
}

interface BudgetCap {
  scope: "tenant" | "caller" | "user";
  scope_id: string;
  max_daily_cost_usd: number | null;
  max_monthly_cost_usd: number | null;
  max_response_cost_usd: number | null;
  enabled: boolean;
}

interface TuningConfig {
  scope: string;
  scope_id: string;
  system_prompt: string | null;
  temperature: number;
  route_mode: string;
}

interface UsageBreakdown {
  group_key: string;
  provider: string;
  calls: number;
  total_cost_usd: number;
}

interface UsageData {
  period_days: number;
  total_cost_usd: number;
  total_calls: number;
  total_tokens: number;
  breakdown: UsageBreakdown[];
}

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  product: string;
  last_login_at: string | null;
}

type Tab = "models" | "budgets" | "tuning" | "usage";

// API helper
function headers(): Record<string, string> {
  const h: Record<string, string> = { "content-type": "application/json" };
  const token = localStorage.getItem("infra_token");
  if (token) h["authorization"] = `Bearer ${token}`;
  return h;
}

async function api<T>(path: string, opts?: RequestInit, base = API_BASE): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    credentials: "include",
    ...opts,
    headers: { ...headers(), ...(opts?.headers as Record<string, string> || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || `API error ${res.status}`);
  }
  return res.json();
}

export function GatewayControlsPanel() {
  const [activeTab, setActiveTab] = useState<Tab>("models");
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const showToast = (message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Tabs */}
      <div className="flex gap-1 px-4 py-3 border-b border-border overflow-x-auto">
        {(["models", "budgets", "tuning", "usage"] as Tab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`
              px-4 py-2 text-sm font-medium rounded-lg capitalize transition-colors whitespace-nowrap
              ${activeTab === tab
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-card"}
            `}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === "models" && <ModelsTab showToast={showToast} />}
        {activeTab === "budgets" && <BudgetsTab showToast={showToast} />}
        {activeTab === "tuning" && <TuningTab showToast={showToast} />}
        {activeTab === "usage" && <UsageTab />}
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg text-sm font-medium shadow-lg z-50
            ${toast.type === "success" ? "bg-success text-success-foreground" : "bg-destructive text-destructive-foreground"}`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

// Models Tab
function ModelsTab({ showToast }: { showToast: (msg: string, type: "success" | "error") => void }) {
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);

  useEffect(() => {
    api<{ models: Model[] }>("/v1/models")
      .then((data) => setModels(data.models || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (model: Model) => {
    setToggling(model.id);
    try {
      await api(`/v1/admin/models/${model.id}`, {
        method: "PUT",
        body: JSON.stringify({ enabled: !model.enabled }),
      });
      setModels((prev) => prev.map((m) => (m.id === model.id ? { ...m, enabled: !m.enabled } : m)));
      showToast(`${model.display_name} ${!model.enabled ? "enabled" : "disabled"}`, "success");
    } catch (e: any) {
      showToast(e.message, "error");
    } finally {
      setToggling(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {models.map((model) => (
          <div
            key={model.id}
            className={`bg-card border border-border rounded-xl p-4 ${model.enabled ? "" : "opacity-60"}`}
          >
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-semibold text-foreground">{model.display_name}</h3>
                <div className="flex gap-1.5 mt-1">
                  <span className="px-1.5 py-0.5 text-xs rounded bg-primary/20 text-primary">
                    {model.provider}
                  </span>
                  <LocationBadge location={model.location} />
                </div>
              </div>
              <Toggle
                checked={model.enabled}
                onChange={() => handleToggle(model)}
                disabled={toggling === model.id}
              />
            </div>

            <div className="space-y-1.5 mb-3">
              <ScoreBar label="Quality" value={model.quality_score} color="bg-accent" />
              <ScoreBar label="Speed" value={model.speed_score} color="bg-primary" />
              <ScoreBar label="Cost" value={100 - Math.min(100, model.input_cost_per_1m_tokens_usd * 20)} color="bg-success" />
            </div>

            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{model.context_window.toLocaleString()} ctx</span>
              <span className="font-mono">
                ${model.input_cost_per_1m_tokens_usd.toFixed(2)} / ${model.output_cost_per_1m_tokens_usd.toFixed(2)} /1M
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Budgets Tab
function BudgetsTab({ showToast }: { showToast: (msg: string, type: "success" | "error") => void }) {
  const [budgets, setBudgets] = useState<BudgetCap[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newBudget, setNewBudget] = useState<Partial<BudgetCap>>({
    scope: "tenant",
    scope_id: "",
    max_daily_cost_usd: null,
    max_monthly_cost_usd: null,
  });

  useEffect(() => {
    api<{ caps: BudgetCap[] }>("/v1/admin/budget")
      .then((data) => setBudgets(data.caps || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!newBudget.scope_id) {
      showToast("Scope ID is required", "error");
      return;
    }
    setSaving(true);
    try {
      await api("/v1/admin/budget", { method: "PUT", body: JSON.stringify(newBudget) });
      setBudgets((prev) => [...prev, newBudget as BudgetCap]);
      setNewBudget({ scope: "tenant", scope_id: "", max_daily_cost_usd: null, max_monthly_cost_usd: null });
      setShowForm(false);
      showToast("Budget added", "success");
    } catch (e: any) {
      showToast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const grouped = budgets.reduce((acc, b) => {
    (acc[b.scope] ||= []).push(b);
    return acc;
  }, {} as Record<string, BudgetCap[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-foreground">Budget Caps</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          {showForm ? "Cancel" : "Add Budget"}
        </button>
      </div>

      {showForm && (
        <div className="bg-card border border-border rounded-xl p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Scope</label>
              <select
                value={newBudget.scope}
                onChange={(e) => setNewBudget((p) => ({ ...p, scope: e.target.value as any }))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground"
              >
                <option value="tenant">tenant</option>
                <option value="caller">caller</option>
                <option value="user">user</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Scope ID</label>
              <input
                type="text"
                value={newBudget.scope_id}
                onChange={(e) => setNewBudget((p) => ({ ...p, scope_id: e.target.value }))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground"
                placeholder="e.g. prod"
              />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Daily Cap (USD)</label>
              <input
                type="number"
                value={newBudget.max_daily_cost_usd ?? ""}
                onChange={(e) => setNewBudget((p) => ({ ...p, max_daily_cost_usd: e.target.value ? parseFloat(e.target.value) : null }))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground font-mono"
              />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Monthly Cap (USD)</label>
              <input
                type="number"
                value={newBudget.max_monthly_cost_usd ?? ""}
                onChange={(e) => setNewBudget((p) => ({ ...p, max_monthly_cost_usd: e.target.value ? parseFloat(e.target.value) : null }))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground font-mono"
              />
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="mt-4 px-4 py-2 text-sm font-medium bg-success text-success-foreground rounded-lg hover:bg-success/90 transition-colors disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      )}

      {(["tenant", "caller", "user"] as const).map((scope) => {
        const items = grouped[scope];
        if (!items?.length) return null;
        return (
          <div key={scope} className="mb-6">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              {scope}
            </h3>
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="text-left px-4 py-2">Scope ID</th>
                    <th className="text-right px-4 py-2">Daily Cap</th>
                    <th className="text-right px-4 py-2">Monthly Cap</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((b, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td className="px-4 py-2 text-sm font-mono text-foreground">{b.scope_id}</td>
                      <td className="px-4 py-2 text-sm font-mono text-right text-foreground">
                        {b.max_daily_cost_usd ? `$${b.max_daily_cost_usd}` : "-"}
                      </td>
                      <td className="px-4 py-2 text-sm font-mono text-right text-foreground">
                        {b.max_monthly_cost_usd ? `$${b.max_monthly_cost_usd}` : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Tuning Tab
function TuningTab({ showToast }: { showToast: (msg: string, type: "success" | "error") => void }) {
  const [configs, setConfigs] = useState<TuningConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<TuningConfig | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api<{ configs: TuningConfig[] }>("/v1/admin/tuning")
      .then((data) => setConfigs(data.configs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api("/v1/admin/tuning", { method: "PUT", body: JSON.stringify(editing) });
      setConfigs((prev) => prev.map((c) => (c.scope === editing.scope && c.scope_id === editing.scope_id ? editing : c)));
      setEditing(null);
      showToast("Config updated", "success");
    } catch (e: any) {
      showToast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="text-lg font-semibold text-foreground mb-4">Tuning Configs</h2>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border text-xs text-muted-foreground">
              <th className="text-left px-4 py-2">Scope</th>
              <th className="text-left px-4 py-2">Scope ID</th>
              <th className="text-left px-4 py-2">System Prompt</th>
              <th className="text-right px-4 py-2">Temp</th>
              <th className="text-left px-4 py-2">Route Mode</th>
              <th className="text-right px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {configs.map((c, i) => (
              <tr key={i} className="border-b border-border last:border-0">
                <td className="px-4 py-2 text-sm text-foreground">{c.scope}</td>
                <td className="px-4 py-2 text-sm font-mono text-foreground">{c.scope_id}</td>
                <td className="px-4 py-2 text-sm text-muted-foreground truncate max-w-[200px]">
                  {c.system_prompt ? c.system_prompt.slice(0, 50) + "..." : "-"}
                </td>
                <td className="px-4 py-2 text-sm font-mono text-right text-foreground">{c.temperature}</td>
                <td className="px-4 py-2 text-sm text-foreground">{c.route_mode}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => setEditing(c)}
                    className="text-xs text-primary hover:underline"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 bg-background/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Edit Config: {editing.scope} / {editing.scope_id}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-1">System Prompt</label>
                <textarea
                  value={editing.system_prompt || ""}
                  onChange={(e) => setEditing({ ...editing, system_prompt: e.target.value })}
                  rows={4}
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Temperature</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="2"
                    value={editing.temperature}
                    onChange={(e) => setEditing({ ...editing, temperature: parseFloat(e.target.value) })}
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Route Mode</label>
                  <select
                    value={editing.route_mode}
                    onChange={(e) => setEditing({ ...editing, route_mode: e.target.value })}
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground"
                  >
                    <option value="chat.balanced">Balanced</option>
                    <option value="chat.fast">Fast</option>
                    <option value="chat.best">Best</option>
                    <option value="chat.cheapest">Cheapest</option>
                    <option value="chat.local_only">Local Only</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 text-sm font-medium bg-card border border-border rounded-lg text-foreground hover:bg-background transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Usage Tab
function UsageTab() {
  const [data, setData] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(7);
  const [groupBy, setGroupBy] = useState("model");

  useEffect(() => {
    setLoading(true);
    api<UsageData>(`/v1/admin/cost?days=${period}&group_by=${groupBy}`)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period, groupBy]);

  const maxCost = data?.breakdown.reduce((max, b) => Math.max(max, b.total_cost_usd), 0) || 1;

  return (
    <div className="p-4">
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <h2 className="text-lg font-semibold text-foreground">Usage</h2>
        <div className="flex gap-2 ml-auto">
          <select
            value={period}
            onChange={(e) => setPeriod(parseInt(e.target.value))}
            className="bg-card border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            className="bg-card border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
          >
            <option value="model">By Model</option>
            <option value="tenant">By Tenant</option>
            <option value="caller">By Caller</option>
            <option value="user">By User</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
        </div>
      ) : data ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="text-xs text-muted-foreground mb-1">Total Cost</div>
              <div className="text-2xl font-semibold font-mono text-foreground">${data.total_cost_usd.toFixed(2)}</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="text-xs text-muted-foreground mb-1">Total Calls</div>
              <div className="text-2xl font-semibold font-mono text-foreground">{data.total_calls.toLocaleString()}</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="text-xs text-muted-foreground mb-1">Total Tokens</div>
              <div className="text-2xl font-semibold font-mono text-foreground">{data.total_tokens.toLocaleString()}</div>
            </div>
          </div>

          {/* Breakdown */}
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4">Breakdown</h3>
            <div className="space-y-3">
              {data.breakdown.map((b, i) => (
                <div key={i}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-foreground">{b.group_key}</span>
                    <span className="font-mono text-muted-foreground">${b.total_cost_usd.toFixed(4)}</span>
                  </div>
                  <div className="h-2 bg-background rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all"
                      style={{ width: `${(b.total_cost_usd / maxCost) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="text-muted-foreground text-center py-10">No usage data available</div>
      )}
    </div>
  );
}

// Users Tab
function UsersTab({ showToast }: { showToast: (msg: string, type: "success" | "error") => void }) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);

  useEffect(() => {
    api<{ users: User[] }>("/admin/users", undefined, AUTH_BASE)
      .then((data) => setUsers(data.users || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleRoleChange = async (user: User, newRole: string) => {
    setUpdating(user.id);
    try {
      await api(`/admin/users/${user.id}`, { method: "PUT", body: JSON.stringify({ role: newRole }) }, AUTH_BASE);
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, role: newRole } : u)));
      showToast(`Role updated to ${newRole}`, "success");
    } catch (e: any) {
      showToast(e.message, "error");
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="text-lg font-semibold text-foreground mb-4">Users</h2>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border text-xs text-muted-foreground">
              <th className="text-left px-4 py-2">User</th>
              <th className="text-left px-4 py-2">Product</th>
              <th className="text-left px-4 py-2">Role</th>
              <th className="text-left px-4 py-2">Last Login</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-border last:border-0">
                <td className="px-4 py-2">
                  <div className="text-sm font-medium text-foreground">{u.name || u.email}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                </td>
                <td className="px-4 py-2 text-sm text-foreground">{u.product}</td>
                <td className="px-4 py-2">
                  <select
                    value={u.role}
                    onChange={(e) => handleRoleChange(u, e.target.value)}
                    disabled={updating === u.id}
                    className="bg-background border border-border rounded px-2 py-1 text-sm text-foreground disabled:opacity-50"
                  >
                    <option value="user">user</option>
                    <option value="admin">admin</option>
                    <option value="platform_admin">platform_admin</option>
                  </select>
                </td>
                <td className="px-4 py-2 text-xs text-muted-foreground font-mono">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : "Never"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// UI Components
function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onChange}
      disabled={disabled}
      className={`
        relative w-9 h-5 rounded-full transition-colors
        ${checked ? "bg-success" : "bg-border"}
        ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
      `}
    >
      <div
        className={`
          absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all
          ${checked ? "left-4" : "left-0.5"}
        `}
      />
    </button>
  );
}

function ScoreBar({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-12 text-muted-foreground">{label}</span>
      <div className="flex-1 h-1 bg-border rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${value}%` }} />
      </div>
      <span className="w-6 text-right font-mono text-muted-foreground">{value}</span>
    </div>
  );
}

function LocationBadge({ location }: { location: "local" | "edge" | "remote" }) {
  const styles = {
    local: "bg-local/20 text-local",
    edge: "bg-edge/20 text-edge",
    remote: "bg-remote/20 text-remote",
  };
  return (
    <span className={`px-1.5 py-0.5 text-xs rounded font-medium ${styles[location]}`}>
      {location}
    </span>
  );
}
